import { initializeApp, getApps, type FirebaseApp } from "firebase/app";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from "firebase/app-check";
import { getAuth, type Auth } from "firebase/auth";
import { getFirestore, type Firestore } from "firebase/firestore";
import { getStorage, type FirebaseStorage } from "firebase/storage";
import { getMessaging, isSupported } from "firebase/messaging";

const firebaseConfig = {
  apiKey: import.meta.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: import.meta.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.NEXT_PUBLIC_FIREBASE_APP_ID,
};

const RECAPTCHA_ENTERPRISE_SITE_KEY = import.meta.env
  .NEXT_PUBLIC_RECAPTCHA_ENTERPRISE_SITE_KEY as string | undefined;
const APP_CHECK_DEBUG_TOKEN = import.meta.env
  .NEXT_PUBLIC_APP_CHECK_DEBUG_TOKEN as string | undefined;

let appCheckInitialized = false;
function ensureAppCheck(app: FirebaseApp): void {
  if (appCheckInitialized) return;
  if (typeof window === "undefined") return;
  if (!RECAPTCHA_ENTERPRISE_SITE_KEY) {
    if (import.meta.env.DEV) {
      console.warn(
        "[App Check] NEXT_PUBLIC_RECAPTCHA_ENTERPRISE_SITE_KEY not set — App Check disabled. Set it in web/.env.local to enable.",
      );
    }
    return;
  }
  if (APP_CHECK_DEBUG_TOKEN && import.meta.env.DEV) {
    (
      self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string }
    ).FIREBASE_APPCHECK_DEBUG_TOKEN = APP_CHECK_DEBUG_TOKEN;
  }
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(RECAPTCHA_ENTERPRISE_SITE_KEY),
    isTokenAutoRefreshEnabled: true,
  });
  appCheckInitialized = true;
}

function getApp(): FirebaseApp {
  const app =
    getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
  ensureAppCheck(app);
  return app;
}

// Lazy singletons — only initialized when accessed in the browser
let _auth: Auth | undefined;
let _db: Firestore | undefined;
let _storage: FirebaseStorage | undefined;

export function getAuthInstance(): Auth {
  if (!_auth) _auth = getAuth(getApp());
  return _auth;
}

export function getDbInstance(): Firestore {
  if (!_db) _db = getFirestore(getApp());
  return _db;
}

export function getStorageInstance(): FirebaseStorage {
  if (!_storage) _storage = getStorage(getApp());
  return _storage;
}

// Convenience getters that match the old API (used in client components only)
export const auth = typeof window !== "undefined" ? getAuthInstance() : (undefined as unknown as Auth);
export const db = typeof window !== "undefined" ? getDbInstance() : (undefined as unknown as Firestore);
export const storage = typeof window !== "undefined" ? getStorageInstance() : (undefined as unknown as FirebaseStorage);

export async function getMessagingInstance() {
  const supported = await isSupported();
  if (supported) {
    return getMessaging(getApp());
  }
  return null;
}

export default getApp;
